import { getCountries, isValidPhoneNumber } from "react-phone-number-input";

// The countries the input can actually have selected. Anything outside this set
// cannot be named in the hint: Intl.DisplayNames answers an unrecognised code
// with a placeholder like "Unknown Region", and "a complete Unknown Region
// number" is not a sentence to show someone filling in a form.
const nameableCountries = new Set<string>(getCountries());

// Intl.DisplayNames covers every ISO 3166-1 region without pulling in a
// dependency.
const countryName = (code: string): string | undefined => {
    if (!nameableCountries.has(code)) return undefined;
    try {
        return (
            new Intl.DisplayNames(["en"], { type: "region" }).of(code) ??
            undefined
        );
    } catch {
        return undefined;
    }
};

/**
 * What to tell someone whose number is not dialable.
 *
 * `selectedCountry` is the country the input is currently showing, and it
 * decides which mistake to name. With a country selected the calling code is
 * already in the field and the digits are what is wrong, so asking for a
 * country code would be nonsense. With none, the field is sitting on a bare
 * "+", and the missing country code is precisely the problem.
 *
 * Naming the country also surfaces the mistake this validation exists to catch:
 * someone who typed a national number without their country code is shown a
 * country they have never been to, and the fix suggests itself.
 */
export const phoneNumberHint = (selectedCountry?: string): string => {
    if (!selectedCountry) {
        return "Include your country code, e.g. +1 415 555 2671.";
    }

    const name = countryName(selectedCountry);

    return name
        ? `That doesn't look like a complete ${name} number — check the digits, or the country beside them.`
        : "That doesn't look like a complete phone number — check the digits, or the country beside them.";
};

/**
 * Whether `value` is present but not a complete, dialable phone number.
 *
 * Deliberately the strict `isValidPhoneNumber` rather than
 * `isPossiblePhoneNumber`. The phone inputs are international-only, so someone
 * who types a national number without their country code has the leading digits
 * consumed as a country calling code instead: a US 5734989801 parses as
 * +57 34989801, a Colombian number. Numbers mangled that way land on a real
 * country carrying a digit count that country's numbering plan does not allow,
 * which is exactly what the strict check rejects and the "possible" check lets
 * through -- `isPossiblePhoneNumber("+5734989801")` is true.
 *
 * An empty value is not malformed: presence is enforced separately by each
 * form's own required-field handling, so a format hint driven off this does not
 * fire on a field the user has not filled in yet.
 */
export const isMalformedPhoneNumber = (value: string): boolean =>
    !!value && !isValidPhoneNumber(value);
